import {
  PROJECT_MARKET_DOMESTIC,
  PROJECT_MARKET_OVERSEAS,
  PROJECT_PRODUCTION_MODE_SD25,
  PROJECT_PRODUCTION_MODE_STANDARD,
  PROJECT_PRODUCTION_MODE_WAN3,
  VIDEO_PROVIDER_ENG_GUAVIDEO,
  VIDEO_PROVIDER_GUAVIDEO,
  VIDEO_PROVIDER_OMINI_SEEDANCE,
  VIDEO_PROVIDER_WANX,
  getVideoProviderLabel,
  isOverseasProjectProfile,
  isSd25Project,
  isWan3Project,
  normalizeProjectProductionMode,
  normalizeVideoProvider,
} from './projectRuntime';
import {
  DEFAULT_SEEDANCE25_PROVIDER,
  normalizeSeedance25SubmissionProvider,
} from './seedance25Capabilities';
import { VIDEO_PROVIDER_VOLCENGINE_SEEDANCE, isDomesticOfficialSeedance } from './domesticSeedanceCapabilities';

export const PROJECT_TYPE_SETTINGS_ENDPOINT = '/api/project/update';
export const PROJECT_TYPE_SETTINGS_METHOD = 'PUT';

export const PROJECT_TYPE_SETTING_DOMESTIC_GUAVIDEO = 'domestic_guavideo';
export const PROJECT_TYPE_SETTING_DOMESTIC_OFFICIAL = 'domestic_official_seedance';
export const PROJECT_TYPE_SETTING_OVERSEAS_ENG = 'overseas_eng';
export const PROJECT_TYPE_SETTING_OVERSEAS_OMINI = 'overseas_omini';
export const PROJECT_TYPE_SETTING_SD25 = 'sd25';
export const PROJECT_TYPE_SETTING_WAN3 = 'wan3';

const createOption = ({
  key,
  title,
  providerLabel,
  description,
  accent,
}) => Object.freeze({
  key,
  id: key,
  value: key,
  title,
  label: title,
  providerLabel,
  displayLabel: providerLabel ? `${title} · ${providerLabel}` : title,
  description,
  accent,
  supportsVideoProviderSelection: false,
});

export const PROJECT_TYPE_OPTIONS = Object.freeze([
  createOption({ key: PROJECT_TYPE_SETTING_DOMESTIC_OFFICIAL, title: '国内官方', providerLabel: 'Seedance 2.0', description: '官方 2.0 / Fast / Mini，共用国内官方素材库', accent: 'var(--color-accent-secondary)' }),
  createOption({
    key: PROJECT_TYPE_SETTING_DOMESTIC_GUAVIDEO,
    title: '国内',
    providerLabel: 'GuaVideo',
    description: '国内 Seedance 2.0 默认线路',
    accent: 'var(--color-accent-secondary)',
  }),
  createOption({
    key: PROJECT_TYPE_SETTING_OVERSEAS_ENG,
    title: '出海',
    providerLabel: 'EngGuaVideo',
    description: '海外 Seedance 2.0；新任务走 Dreamina v3 代理，历史任务仍按原线路查询',
    accent: 'var(--color-success)',
  }),
  createOption({
    key: PROJECT_TYPE_SETTING_OVERSEAS_OMINI,
    title: '出海',
    providerLabel: 'GuaOmini',
    description: '海外 Seedance 2.0；新任务走 Dreamina v3 代理，历史任务仍按原线路查询',
    accent: 'var(--color-info, #38bdf8)',
  }),
  createOption({
    key: PROJECT_TYPE_SETTING_SD25,
    title: '即梦 2.5',
    providerLabel: '厂商生成时选择',
    description: '30 秒剧情规划；具体 2.5 厂商在实际生成时选择',
    accent: 'var(--color-accent-primary)',
  }),
  createOption({
    key: PROJECT_TYPE_SETTING_WAN3,
    title: 'Wan3.0 项目',
    providerLabel: '万相 3.0',
    description: 'Wan3.0 独立 30 秒提示词与视频链路',
    accent: '#6366f1',
  }),
]);

// Backward-compatible name for callers that were built while this utility was introduced.
export const PROJECT_TYPE_SETTING_OPTIONS = PROJECT_TYPE_OPTIONS;

const RUNTIME_SETTINGS_BY_KEY = new Map([
  [PROJECT_TYPE_SETTING_DOMESTIC_OFFICIAL, Object.freeze({ productionMode: PROJECT_PRODUCTION_MODE_STANDARD, projectMarket: PROJECT_MARKET_DOMESTIC, contentLanguage: 'zh-CN', videoProvider: VIDEO_PROVIDER_VOLCENGINE_SEEDANCE })],
  [PROJECT_TYPE_SETTING_DOMESTIC_GUAVIDEO, Object.freeze({
    productionMode: PROJECT_PRODUCTION_MODE_STANDARD,
    projectMarket: PROJECT_MARKET_DOMESTIC,
    contentLanguage: 'zh-CN',
    videoProvider: VIDEO_PROVIDER_GUAVIDEO,
  })],
  [PROJECT_TYPE_SETTING_OVERSEAS_ENG, Object.freeze({
    productionMode: PROJECT_PRODUCTION_MODE_STANDARD,
    projectMarket: PROJECT_MARKET_OVERSEAS,
    contentLanguage: 'en-US',
    videoProvider: VIDEO_PROVIDER_ENG_GUAVIDEO,
  })],
  [PROJECT_TYPE_SETTING_OVERSEAS_OMINI, Object.freeze({
    productionMode: PROJECT_PRODUCTION_MODE_STANDARD,
    projectMarket: PROJECT_MARKET_OVERSEAS,
    contentLanguage: 'en-US',
    videoProvider: VIDEO_PROVIDER_OMINI_SEEDANCE,
  })],
  [PROJECT_TYPE_SETTING_SD25, Object.freeze({
    productionMode: PROJECT_PRODUCTION_MODE_SD25,
    projectMarket: PROJECT_MARKET_DOMESTIC,
    contentLanguage: 'zh-CN',
    videoProvider: DEFAULT_SEEDANCE25_PROVIDER,
  })],
  [PROJECT_TYPE_SETTING_WAN3, Object.freeze({
    productionMode: PROJECT_PRODUCTION_MODE_WAN3,
    projectMarket: PROJECT_MARKET_DOMESTIC,
    contentLanguage: 'zh-CN',
    videoProvider: VIDEO_PROVIDER_WANX,
  })],
]);

const OPTIONS_BY_ID = new Map(
  PROJECT_TYPE_OPTIONS.map((option) => [option.key, option]),
);

const optionIdOf = (value) => (
  typeof value === 'object' && value !== null
    ? String(value.id || value.value || '').trim()
    : String(value || '').trim()
);

export const getProjectTypeSettingOption = (value) => (
  OPTIONS_BY_ID.get(optionIdOf(value)) || null
);

const resolveProjectTypeSettingOption = (project = {}) => {
  const productionMode = normalizeProjectProductionMode(project);
  if (productionMode === PROJECT_PRODUCTION_MODE_WAN3) {
    return getProjectTypeSettingOption(PROJECT_TYPE_SETTING_WAN3);
  }
  if (productionMode === PROJECT_PRODUCTION_MODE_SD25) {
    return getProjectTypeSettingOption(PROJECT_TYPE_SETTING_SD25);
  }

  if (isWan3Project(project)) {
    return getProjectTypeSettingOption(PROJECT_TYPE_SETTING_WAN3);
  }
  if (isSd25Project(project)) {
    return getProjectTypeSettingOption(PROJECT_TYPE_SETTING_SD25);
  }

  const provider = normalizeVideoProvider(project?.videoProvider);
  if (isDomesticOfficialSeedance(provider)) return getProjectTypeSettingOption(PROJECT_TYPE_SETTING_DOMESTIC_OFFICIAL);
  if (provider === VIDEO_PROVIDER_OMINI_SEEDANCE) {
    return getProjectTypeSettingOption(PROJECT_TYPE_SETTING_OVERSEAS_OMINI);
  }
  if (provider === VIDEO_PROVIDER_ENG_GUAVIDEO) {
    return getProjectTypeSettingOption(PROJECT_TYPE_SETTING_OVERSEAS_ENG);
  }
  if (provider === VIDEO_PROVIDER_GUAVIDEO) {
    return getProjectTypeSettingOption(PROJECT_TYPE_SETTING_DOMESTIC_GUAVIDEO);
  }

  return isOverseasProjectProfile(project)
    ? getProjectTypeSettingOption(PROJECT_TYPE_SETTING_OVERSEAS_ENG)
    : getProjectTypeSettingOption(PROJECT_TYPE_SETTING_DOMESTIC_GUAVIDEO);
};

export const resolveProjectTypeSetting = (project = {}) => {
  const option = resolveProjectTypeSettingOption(project);
  const runtimeSettings = RUNTIME_SETTINGS_BY_KEY.get(option.key);
  const selectedVideoProvider = option.key === PROJECT_TYPE_SETTING_SD25
    ? normalizeSeedance25SubmissionProvider(project?.videoProvider) || DEFAULT_SEEDANCE25_PROVIDER
    : runtimeSettings.videoProvider;
  return {
    ...option,
    selectedVideoProvider,
    selectedVideoProviderLabel: getVideoProviderLabel(selectedVideoProvider),
  };
};

export const getProjectTypeSelection = (project = {}) => (
  resolveProjectTypeSettingOption(project).key
);

const normalizeProjectId = (projectId) => {
  const parsed = Number(projectId);
  if (!Number.isSafeInteger(parsed) || parsed <= 0) {
    throw new Error('projectId 必须是有效的正整数');
  }
  return parsed;
};

export const buildProjectTypeUpdatePayload = ({
  projectId,
  selection,
  currentProject,
  // Temporary compatibility for the first integration draft.
  optionId,
} = {}) => {
  const option = getProjectTypeSettingOption(selection || optionId);
  if (!option) throw new Error('请选择有效的项目类型');
  const runtimeSettings = RUNTIME_SETTINGS_BY_KEY.get(option.key);

  const currentSeedance25Provider = getProjectTypeSelection(currentProject) === PROJECT_TYPE_SETTING_SD25
    ? normalizeSeedance25SubmissionProvider(currentProject?.videoProvider)
    : '';
  const selectedVideoProvider = option.key === PROJECT_TYPE_SETTING_SD25
    ? currentSeedance25Provider || DEFAULT_SEEDANCE25_PROVIDER
    : runtimeSettings.videoProvider;

  return {
    projectId: normalizeProjectId(projectId),
    productionMode: runtimeSettings.productionMode,
    projectMarket: runtimeSettings.projectMarket,
    contentLanguage: runtimeSettings.contentLanguage,
    videoProvider: selectedVideoProvider,
  };
};
